import { useLayoutEffect, useRef } from 'react'

/**
 * Scales an A4 document sheet down to fit a narrow screen.
 *
 * The sheet is always a true 210 mm so the preview is exactly the printed page; on a
 * phone that is far wider than the viewport, so the whole thing is transform-scaled
 * rather than reflowed. Printing resets the transform (see the print block in
 * index.css), so paper output is unaffected.
 *
 * Shared by the landlord's document view and the guest's public copy — they must
 * render identically, because the guest prints what the landlord approved.
 */
export function useDocScale<T extends HTMLElement, S extends HTMLElement>(
  deps: unknown[] = [],
): { viewportRef: React.RefObject<T | null>; sheetRef: React.RefObject<S | null> } {
  const viewportRef = useRef<T | null>(null)
  const sheetRef = useRef<S | null>(null)

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const sheet = sheetRef.current
    if (!viewport || !sheet) return

    const measure = () => {
      const available = viewport.clientWidth - 32   // the p-4 padding
      const natural = sheet.offsetWidth
      const scale = natural > 0 ? Math.min(1, available / natural) : 1
      viewport.style.setProperty('--doc-scale', String(scale))
      viewport.style.setProperty('--doc-height', `${sheet.offsetHeight * scale}px`)
    }
    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(viewport)
    ro.observe(sheet)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { viewportRef, sheetRef }
}
